import client from '@xFrame4/business/GraphQlClient';
import BusinessEntity from '@xFrame4/business/base/BusinessEntity';
import { imageInfoGraphQlDetails } from '@xFrame4/business/Constants';
import { ImageInfo } from '@xFrame4/business/Types';
import { ManageImageGalleryOperation } from './ImagesConstants';

export enum ImageSizeName
{
    original = 'original',
    // Keep aspect ratio
    icon = 'icon',
    verySmall = 'verySmall',
    small = 'small',
    medium = 'medium',
    large = 'large',
    veryLarge = 'veryLarge',
    thumbnail = 'thumbnail',
    // Aspect ratio 1:1
    iconSquare = 'iconSquare',
    verySmallSquare = 'verySmallSquare',
    smallSquare = 'smallSquare',
    mediumSquare = 'mediumSquare',
    largeSquare = 'largeSquare',
    veryLargeSquare = 'veryLargeSquare',
    thumbnailSquare = 'thumbnailSquare',
    // Aspect ratio 2:1
    icon21 = 'icon21',
    verySmall21 = 'verySmall21',
    small21 = 'small21',
    medium21 = 'medium21',
    large21 = 'large21',
    veryLarge21 = 'veryLarge21',
    thumbnail21 = 'thumbnail21',
    // Aspect ratio 16:9
    icon169 = 'icon169',
    verySmall169 = 'verySmall169',
    small169 = 'small169',
    medium169 = 'medium169',
    large169 = 'large169',
    veryLarge169 = 'veryLarge169',
    thumbnail169 = 'thumbnail169',
    // Aspect ratio 4:3
    icon43 = 'icon43',
    verySmall43 = 'verySmall43',
    small43 = 'small43',
    medium43 = 'medium43',
    large43 = 'large43',
    veryLarge43 = 'veryLarge43',
    thumbnail43 = 'thumbnail43',
    // Aspect ratio 9:16
    icon916 = 'icon916',
    verySmall916 = 'verySmall916',
    small916 = 'small916',
    medium916 = 'medium916',
    large916 = 'large916',
    veryLarge916 = 'veryLarge916',
    thumbnail916 = 'thumbnail916'
}

export class ImageGallery
{
    /** The entity this gallery belongs to (eg: product, showroom, etc.). */
    relatedObject: BusinessEntity;
    /** The name of the image gallery (eg: ProductVariantImageGallery, ShowroomImageGallery, etc.). */
    imageGalleryName: string;
    images: GalleryImage[] = [];
    /** The GraphQL field details to be added to the galleryImages field on the related entity. */
    static graphQlGalleryImageFieldDetails = `
        id
        orderNo
        isDefault
        ${ImageGallery.buildMultiSizeImageGraphQlDetails()}
    `;

    /**
     * Constructor
     * 
     * @param relatedObject The entity this gallery belongs to (eg: product, showroom, etc.).
     * @param imageGalleryName The name of the image gallery (eg: ProductVariantImageGallery, ShowroomImageGallery, etc.).
     */
    constructor(relatedObject: BusinessEntity, imageGalleryName: string)
    {
        this.relatedObject = relatedObject;
        this.imageGalleryName = imageGalleryName;
    }

    /**
    * Load the gallery images from a GraphQL object.
    * 
    * @param gqlGalleryImages 
    */
    loadImagesFromGraphQl(gqlGalleryImages: any[])
    {
        this.images = [];

        for (let gqlGalleryImage of gqlGalleryImages)
        {
            let galleryImage = this.loadImageFromGraphQL(gqlGalleryImage);

            this.images.push(galleryImage);
        }
    }

    /**
    * Load a gallery image from a GraphQL object.
    * 
    * @param gqlGalleryImage 
    */
    loadImageFromGraphQL(gqlGalleryImage: any)
    {
        // base fields
        let galleryImage: any = {
            id: gqlGalleryImage.id,
            orderNo: gqlGalleryImage.orderNo,
            isDefault: gqlGalleryImage.isDefault
        };

        // multi size image fields
        for (let size of Object.values(ImageSizeName))
        {
            galleryImage[size] = gqlGalleryImage[size];
        }

        return galleryImage as GalleryImage;
    }

    /**
     * Get the default image in the gallery.
     * 
     * @returns The default image in the gallery if found else the first image. Undefined if no images.
     */
    getDefaultImage(): GalleryImage | undefined
    {
        let defaultImage = this.images.find(image => image.isDefault);

        if (defaultImage == undefined && this.images.length > 0)
        {
            defaultImage = this.images[0];
        }

        return defaultImage;
    }

    /**
     * Executes the management mutation.
     * 
     * @param variables 
     * @returns The mutation result.
     */
    async mutate(variables: any)
    {
        const { gql } = await import('@apollo/client');

        let graphQlGalleryImageDetailsFragment = `
            fragment GalleryImageDetailsFragment on GalleryImage {
                ${ImageGallery.graphQlGalleryImageFieldDetails}
            }
        `;

        let mutation = `
        mutation Manage${this.imageGalleryName}($relatedObjectId: Int!, $operation: String!, $galleryImageId: Int, $galleryImageIds: [Int], $imageFile: Upload, $imageFiles: [Upload], $moveFrom: Int, $moveTo: Int, $newDefaultImageId: Int) {
            manage${this.imageGalleryName}(relatedObjectId: $relatedObjectId, operation: $operation, galleryImageId: $galleryImageId, galleryImageIds: $galleryImageIds, imageFile: $imageFile, imageFiles: $imageFiles, moveFrom: $moveFrom, moveTo: $moveTo, newDefaultImageId: $newDefaultImageId) {
                success
                galleryImages {
                    ...GalleryImageDetailsFragment
                }
            }
        }
        ${graphQlGalleryImageDetailsFragment}
        `;

        let { data } = await client.mutate({
            mutation: gql(mutation),
            variables: variables
        });

        return data[`manage${this.imageGalleryName}`];
    }

    /**
    * Add an image to the gallery.
    * 
    * @param imageFile 
    */
    async addImage(imageFile: File)
    {
        let variables = {
            relatedObjectId: this.relatedObject.id as number,
            operation: ManageImageGalleryOperation.Add,
            imageFile: imageFile
        }

        let result = await this.mutate(variables);

        if (result.success)
        {
            this.loadImagesFromGraphQl(result.galleryImages);
        }
    }

    /**
    * Add images to the gallery.
    * 
    * @param imageFiles 
    */
    async addImages(imageFiles: FileList)
    {
        let variables = {
            relatedObjectId: this.relatedObject.id as number,
            operation: ManageImageGalleryOperation.AddBulk,
            imageFiles: imageFiles
        };

        let result = await this.mutate(variables);

        if (result.success)
        {
            this.loadImagesFromGraphQl(result.galleryImages);
        }
    }

    /**
    * Delete an image from the gallery
    * 
    * @param galleryImageId 
    */
    async deleteImage(galleryImageId: number)
    {
        let variables = {
            relatedObjectId: this.relatedObject.id as number,
            operation: ManageImageGalleryOperation.Delete,
            galleryImageId: galleryImageId
        };

        let result = await this.mutate(variables);

        if (result.success)
        {
            this.loadImagesFromGraphQl(result.galleryImages);
        }
    }

    /**
    * Delete images from the gallery
    * 
    * @param galleryImageIds 
    */
    async deleteImages(galleryImageIds: number)
    {
        let variables = {
            relatedObjectId: this.relatedObject.id as number,
            operation: ManageImageGalleryOperation.DeleteBulk,
            galleryImageIds: galleryImageIds
        }

        let result = await this.mutate(variables);

        if (result.success)
        {
            this.loadImagesFromGraphQl(result.galleryImages);
        }
    }

    /**
    * Move an image inside the gallery (change the orderNo).
    * 
    * @param moveFrom Move from orderNo
    * @param moveTo Move to orderNo
    */
    async moveImage(moveFrom: number, moveTo: number)
    {
        let variables = {
            relatedObjectId: this.relatedObject.id as number,
            operation: ManageImageGalleryOperation.Move,
            moveFrom: moveFrom,
            moveTo: moveTo
        }

        let result = await this.mutate(variables);

        if (result.success)
        {
            this.loadImagesFromGraphQl(result.galleryImages);
        }
    }

    /**
     * Set the default image inside the gallery.
     * 
     * @param newDefaultImageId The ID of the new default image.
     */
    async setDefaultImage(newDefaultImageId: number)
    {
        let variables = {
            relatedObjectId: this.relatedObject.id as number,
            operation: ManageImageGalleryOperation.SetDefault,
            newDefaultImageId: newDefaultImageId
        }

        let result = await this.mutate(variables);

        if (result.success)
        {
            this.loadImagesFromGraphQl(result.galleryImages);
        }
    }

    /** The GraphQL query field details for a multi size web image. */
    static buildMultiSizeImageGraphQlDetails()
    {
        let multiSizeImageGraphQlDetails = `
        `;

        for (let size of Object.values(ImageSizeName))
        {
            multiSizeImageGraphQlDetails += `
                ${size} {
                    ${imageInfoGraphQlDetails}
                }
            `;
        }

        return multiSizeImageGraphQlDetails;
    }
}

/** Represents an image on the web with multiple sizes. */
export interface MultiSizeImage 
{
    original: ImageInfo;
    // Keep aspect ratio
    icon: ImageInfo;
    verySmall: ImageInfo;
    small: ImageInfo;
    medium: ImageInfo;
    large: ImageInfo;
    veryLarge: ImageInfo;
    thumbnail: ImageInfo;
    // Aspect ratio 1:1
    iconSquare: ImageInfo;
    verySmallSquare: ImageInfo;
    smallSquare: ImageInfo;
    mediumSquare: ImageInfo;
    largeSquare: ImageInfo;
    veryLargeSquare: ImageInfo;
    thumbnailSquare: ImageInfo;
    // Aspect ratio 2:1
    icon21: ImageInfo;
    verySmall21: ImageInfo;
    small21: ImageInfo;
    medium21: ImageInfo;
    large21: ImageInfo;
    veryLarge21: ImageInfo;
    thumbnail21: ImageInfo;
    // Aspect ratio 16:9
    icon169: ImageInfo;
    verySmall169: ImageInfo;
    small169: ImageInfo;
    medium169: ImageInfo;
    large169: ImageInfo;
    veryLarge169: ImageInfo;
    thumbnail169: ImageInfo;
    // Aspect ratio 4:3
    icon43: ImageInfo;
    verySmall43: ImageInfo;
    small43: ImageInfo;
    medium43: ImageInfo;
    large43: ImageInfo;
    veryLarge43: ImageInfo;
    thumbnail43: ImageInfo;
    // Aspect ratio 9:16
    icon916: ImageInfo;
    verySmall916: ImageInfo;
    small916: ImageInfo;
    medium916: ImageInfo;
    large916: ImageInfo;
    veryLarge916: ImageInfo;
    thumbnail916: ImageInfo;
}

/** Represents an image inside a gallery. */
export interface GalleryImage extends MultiSizeImage
{
    id: number;
    orderNo: number;
    isDefault: boolean;
}

export default ImageGallery;